import { Handle, Position } from 'reactflow';
import { Icon } from '@scale/scaleui';
import { IconName, IconPrefix } from '@fortawesome/fontawesome-svg-core';
import _ from 'lodash';
import { ResumeScreeningActionNodeStatus } from '../constants';

type ActionNodeData = {
  label: string;
  actionDetails: string[];
  status: ResumeScreeningActionNodeStatus;
};

const getNodeClass = (status: ResumeScreeningActionNodeStatus) => {
  let nodeClass = `flex flex-col p-2 bg-neutral-0 border rounded-lg min-h-[50px] min-w-[200px] max-w-[200px]`;
  if (status === ResumeScreeningActionNodeStatus.Active) {
    nodeClass += `shadow-md border-primary-300`;
  } else if (status === ResumeScreeningActionNodeStatus.Disabled) {
    nodeClass += ` shadow-sm border-neutral-300 hover:border-primary-300 pointer-events-none opacity-50`;
  } else {
    nodeClass += `shadow-sm border-neutral-300 hover:border-primary-300`;
  }
  return nodeClass;
};

const getColorClass = (actionType: string) => {
  switch (actionType) {
    case 'Pass':
      return 'bg-success-50 rounded-lg text-success-800 font-medium px-2 mx-1 whitespace-nowrap';
    case 'Fail':
      return 'bg-danger-50 rounded-lg text-danger-800 font-medium px-2 mx-1 whitespace-nowrap';
    case 'Human Review':
      return 'bg-neutral-100 rounded-lg text-neutral-800 font-medium px-2 mx-1 whitespace-nowrap';
    default:
      return 'text-neutral-600 rounded-lg whitespace-nowrap';
  }
};

const getIconForActionDetail = (actionDetail: string): [IconPrefix, IconName] => {
  if (actionDetail.includes('Move Stages')) {
    return ['far', 'timeline'];
  } else if (actionDetail.includes('Edit Tags')) {
    return ['far', 'tags'];
  } else {
    return ['far', 'file-user'];
  }
};

export const ResumeScreenActionNode = ({ data }: { data: ActionNodeData }) => {
  const { label, actionDetails, status } = data;

  return (
    <div className={getNodeClass(status)}>
      <Handle
        type="target"
        position={Position.Top}
        style={{ left: '50%', transform: 'translateX(-50%)' }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ left: '50%', transform: 'translateX(-50%)' }}
      />
      <div className="flex flex-row items-center text-xs font-semibold mb-2">
        If <span className={getColorClass(label)}>{label}</span> then:
      </div>
      {actionDetails?.map((detail: string, index: number) => (
        <div
          key={index}
          className="flex flex-row items-center text-xs p-2 border border-neutral-300 rounded my-1"
        >
          <div className="bg-primary-50 p-0.5 rounded-md">
            <Icon icon={getIconForActionDetail(detail)} size="sm" variant="primary" />
          </div>
          <div className="ml-2">{detail}</div>
        </div>
      ))}
      {status !== ResumeScreeningActionNodeStatus.Disabled && _.isEmpty(actionDetails) && (
        <div className="flex flex-row items-center text-xs p-2 border border-dashed border-neutral-300 rounded my-1 hover:border-primary-500 hover:bg-primary-50">
          <div className="bg-primary-50 p-0.5 rounded-md">
            <Icon icon={['far', 'plus']} size="sm" variant="primary" />
          </div>
          <div className="ml-2 text-primary-500">Add Action</div>
        </div>
      )}
      {status === ResumeScreeningActionNodeStatus.Disabled && (
        <div className="flex flex-row items-center text-xs p-2 border border-dashed border-neutral-300 rounded my-1 hover:border-primary-500 hover:bg-primary-50">
          <div className="ml-2 text-neutral-500">Configure in OCP directly</div>
        </div>
      )}
    </div>
  );
};
